import React, { useState, useMemo, useEffect } from "react";
import { useSkinsMasterContext } from "./SkinsMaster.jsx";
import { useQueries } from "@tanstack/react-query";
import { q_skins_master_txns, polytxnidlink } from "@/queries/queries.jsx";
import { getv, nils, iso_format } from "../../utils/utils.jsx";
import { TokenIcon } from "../../components/utilityComps.jsx";
import { InlineLoginSection } from "@/wrappers/AuthWrapper.jsx";
import { Link } from "react-router-dom";
import {
  Flame,
  ShoppingCart,
  Package,
  ExternalLink,
  Filter,
  Search,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

export const Ledger = () => {
  const skm_con = useSkinsMasterContext();
  const { vault, isauvault } = skm_con;
  const exisvault = !nils(vault);

  const [current_page, set_current_page] = useState(1);
  const [search_text, set_search_text] = useState("");
  const [filter_type, set_filter_type] = useState("all"); // all, burned, purchased, pack_purchased
  const [show_filters, set_show_filters] = useState(false);

  // Reset to page 1 when filters change
  useEffect(() => {
    set_current_page(1);
  }, [filter_type, search_text]);

  const [qo_txns] = useQueries({
    queries: [
      q_skins_master_txns(
        { vault, page: current_page },
        { enabled: exisvault },
      ),
    ],
  });

  const txns_data = useMemo(() => {
    return getv(qo_txns, "data.result", {});
  }, [qo_txns.dataUpdatedAt]);

  const {
    txns = [],
    page = 1,
    last_page = 1,
    total_count = 0,
    skins_info_map = {},
  } = txns_data;

  // Filter transactions based on search and type
  const filtered_txns = useMemo(() => {
    let filtered = txns;

    // Filter by type
    if (filter_type !== "all") {
      filtered = filtered.filter((txn) => {
        if (filter_type === "burned") return txn.type === "skin_burned";
        if (filter_type === "purchased") return txn.type === "skin_purchased";
        if (filter_type === "pack_purchased")
          return txn.type === "skin_pack_purchased";
        return true;
      });
    }

    // Filter by search text
    if (search_text.length >= 2) {
      const search_lower = search_text.toLowerCase();
      filtered = filtered.filter((txn) => {
        // Search in transaction hash
        if (txn.hash.toLowerCase().includes(search_lower)) return true;

        // Search in skin names
        if (txn.skins) {
          return txn.skins.some((skin) =>
            skin.name?.toLowerCase().includes(search_lower),
          );
        }

        // Search in event type
        if (txn.event.toLowerCase().includes(search_lower)) return true;

        return false;
      });
    }

    return filtered;
  }, [txns, filter_type, search_text]);

  const get_transaction_icon = (type) => {
    switch (type) {
      case "skin_burned":
        return <Flame className="w-5 h-5 text-orange-500" />;
      case "skin_purchased":
        return <ShoppingCart className="w-5 h-5 text-blue-500" />;
      case "skin_pack_purchased":
        return <Package className="w-5 h-5 text-purple-500" />;
      default:
        return <div className="w-5 h-5 bg-gray-500 rounded-full" />;
    }
  };

  const get_transaction_title = (txn) => {
    switch (txn.type) {
      case "skin_burned":
        return "Skin Burned";
      case "skin_purchased":
        return "Skin Purchased";
      case "skin_pack_purchased":
        return "Pack Purchased";
      default:
        return txn.event;
    }
  };

  const get_transaction_subtitle = (txn) => {
    if (txn.type === "skin_burned") {
      const reward = txn.connects?.reward_sgc || 0;
      return `+${reward} SGC reward`;
    }

    if (txn.type === "skin_purchased" || txn.type === "skin_pack_purchased") {
      const connects = txn.connects;
      const token = connects?.token;
      const amt_tok = connects?.amt_tok;
      const amt_usd = connects?.amt_usd;

      if (token === "SGC") {
        return `-${amt_tok} SGC`;
      } else if (amt_usd) {
        return `-$${amt_usd} (${amt_tok} ${token})`;
      }
    }

    return "";
  };

  // Show login prompt if not authenticated
  if (!isauvault) {
    return (
      <div className="w-full max-w-2xl mx-auto">
        <div className="bg-slate-600/20 border border-slate-500/30 rounded-xl backdrop-blur-md">
          <InlineLoginSection
            customHeading="Login to View Transaction History"
            customDescription="Connect your wallet to access your complete transaction ledger and track all skin-related activities."
            customContent={
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                <div className="text-purple-400 font-digi text-sm text-center space-y-2">
                  <div className="font-bold">📊 Ledger Features:</div>
                  <div className="text-xs text-gray-300">
                    • Track all skin purchases
                    <br />
                    • View skin pack purchases
                    <br />
                    • Monitor burn transactions & SGC rewards
                    <br />
                    • Search and filter transaction history
                  </div>
                </div>
              </div>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="text-center text-white mb-8">
        <h2 className="text-3xl font-digi mb-2">Transaction Ledger</h2>
        <p className="text-gray-400">
          Track your skin purchases, burns, and rewards
        </p>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        {/* Search Bar with Filter Toggle */}
        <div className="flex items-center space-x-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search transactions by hash, skin name, or event..."
              value={search_text}
              onChange={(e) => set_search_text(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-slate-600/40 border border-slate-500/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 backdrop-blur-md"
            />
          </div>
          <button
            onClick={() => set_show_filters(!show_filters)}
            className={`px-4 py-3 rounded-lg border transition-all duration-200 backdrop-blur-md flex items-center space-x-2 ${
              show_filters || filter_type !== "all"
                ? "bg-blue-600/60 border-blue-500/50 text-white"
                : "bg-slate-600/40 border-slate-500/30 text-gray-300 hover:bg-slate-500/50"
            }`}
          >
            <Filter className="w-4 h-4" />
            <span className="font-medium">Filter</span>
          </button>
        </div>

        {/* Collapsible Filters */}
        {show_filters && (
          <div className="bg-slate-600/20 border border-slate-500/30 rounded-lg p-4 backdrop-blur-md">
            <div className="flex flex-wrap gap-3">
              {[
                { key: "all", label: "All Transactions" },
                { key: "purchased", label: "Skin Purchases" },
                { key: "pack_purchased", label: "Pack Purchases" },
                { key: "burned", label: "Skin Burns" },
              ].map((type) => (
                <button
                  key={type.key}
                  onClick={() => set_filter_type(type.key)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    filter_type === type.key
                      ? "bg-blue-600 text-white shadow-md"
                      : "bg-slate-700/40 text-gray-300 hover:bg-slate-600/60 hover:text-white"
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Results Count */}
        {(search_text.length >= 2 || filter_type !== "all") && (
          <div className="text-gray-400 text-sm">
            Showing {filtered_txns.length} of {total_count} transactions
          </div>
        )}
      </div>

      {/* Transactions List */}
      <div className="space-y-4">
        {qo_txns.isLoading ? (
          <div className="text-center text-white py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            <p className="mt-4">Loading transactions...</p>
          </div>
        ) : filtered_txns.length === 0 ? (
          <div className="text-center text-white py-12">
            <p className="text-gray-400">
              {search_text.length >= 2 || filter_type !== "all"
                ? "No transactions found matching your filters"
                : "No transactions found"}
            </p>
          </div>
        ) : (
          filtered_txns.map((txn) => (
            <div
              key={txn.id}
              className="bg-slate-600/20 border border-slate-500/30 rounded-lg p-4 backdrop-blur-md hover:bg-slate-500/20 transition-all duration-200"
            >
              {/* Grid Layout for main transaction info */}
              <div className="grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center mb-4">
                {/* 1st Cell: Icon + Transaction Type */}
                <div className="flex items-center space-x-3">
                  <div>{get_transaction_icon(txn.type)}</div>
                  <div className="text-white font-semibold text-sm">
                    {get_transaction_title(txn)}
                  </div>
                </div>

                {/* 2nd Cell: Token Balance Changes */}
                <div className="text-left">
                  <div className="text-gray-300 font-medium">
                    {get_transaction_subtitle(txn)}
                  </div>
                </div>

                {/* 3rd Cell: Date */}
                <div className="text-right">
                  <div className="text-gray-300 text-sm">
                    {iso_format(txn.date, "DD-MMM'YY\n hh:mma")}
                  </div>
                </div>

                {/* 4th Cell: Blockchain Link with TokenIcon */}
                <div className="flex items-center justify-end">
                  <Link
                    to={polytxnidlink(txn.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-blue-400 hover:text-blue-300 transition-colors duration-200"
                  >
                    <TokenIcon token="MATIC" size="xs" />
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              {/* Full Row: Skins List */}
              {((txn.skins && txn.skins.length > 0) ||
                (txn.type === "skin_burned" && txn.connects?.rvsid)) && (
                <div className="border-t border-slate-500/20 pt-3">
                  <div className="text-sm text-gray-400 mb-2">
                    {txn.type === "skin_burned" ? "Burned:" : "Items:"}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {txn.type === "skin_burned" && txn.connects?.rvsid ? (
                      // Handle skin_burned with connects data
                      <div className="flex flex-col px-3 py-2 bg-slate-700/40 rounded-lg border border-slate-600/50">
                        <div className="text-white text-sm font-medium">
                          {skins_info_map[txn.connects.infoid]?.skin ||
                            "Unknown Skin"}
                        </div>
                        <div className="text-gray-400 text-xs">
                          {txn.connects.rvsid}
                        </div>
                      </div>
                    ) : (
                      // Handle purchases with skins array
                      txn.skins?.map((skin, index) => (
                        <div
                          key={index}
                          className="flex flex-col px-3 py-2 bg-slate-700/40 rounded-lg border border-slate-600/50"
                        >
                          <div className="text-white text-sm font-medium">
                            {skin.name}
                          </div>
                          <div className="text-gray-400 text-xs">
                            {skin.rvsid}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Enhanced Pagination */}
      {(last_page > 1 || total_count > 0) && (
        <div className="mt-8">
          {/* Pagination Info */}
          <div className="text-center text-gray-400 text-sm mb-4">
            Showing page {current_page} of {last_page} ({total_count} total
            transactions)
          </div>

          {/* Pagination Controls */}
          {last_page > 1 ? (
            <div className="flex items-center justify-center space-x-2">
              {/* Latest Button */}
              <button
                onClick={() => set_current_page(1)}
                disabled={current_page === 1}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  current_page === 1
                    ? "bg-slate-700/40 text-gray-500 cursor-not-allowed"
                    : "bg-slate-600/40 text-white hover:bg-slate-500/50 border border-slate-500/30"
                }`}
              >
                Latest
              </button>

              {/* Previous Button */}
              <button
                onClick={() =>
                  set_current_page((prev) => Math.max(1, prev - 1))
                }
                disabled={current_page === 1}
                className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  current_page === 1
                    ? "bg-slate-700/40 text-gray-500 cursor-not-allowed"
                    : "bg-slate-600/40 text-white hover:bg-slate-500/50 border border-slate-500/30"
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
                <span>Prev</span>
              </button>

              {/* Page Numbers */}
              <div className="flex items-center space-x-1">
                {/* Show first page if we're far from it */}
                {current_page > 3 && (
                  <>
                    <button
                      onClick={() => set_current_page(1)}
                      className="px-3 py-2 rounded-lg text-sm font-medium bg-slate-600/40 text-white hover:bg-slate-500/50 border border-slate-500/30 transition-all duration-200"
                    >
                      1
                    </button>
                    {current_page > 4 && (
                      <span className="text-gray-400 px-2">...</span>
                    )}
                  </>
                )}

                {/* Show pages around current page */}
                {Array.from({ length: Math.min(5, last_page) }, (_, i) => {
                  let page_num;
                  if (last_page <= 5) {
                    page_num = i + 1;
                  } else if (current_page <= 3) {
                    page_num = i + 1;
                  } else if (current_page >= last_page - 2) {
                    page_num = last_page - 4 + i;
                  } else {
                    page_num = current_page - 2 + i;
                  }

                  if (page_num < 1 || page_num > last_page) return null;

                  return (
                    <button
                      key={page_num}
                      onClick={() => set_current_page(page_num)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        current_page === page_num
                          ? "bg-blue-600 text-white shadow-md"
                          : "bg-slate-600/40 text-white hover:bg-slate-500/50 border border-slate-500/30"
                      }`}
                    >
                      {page_num}
                    </button>
                  );
                })}

                {/* Show last page if we're far from it */}
                {current_page < last_page - 2 && (
                  <>
                    {current_page < last_page - 3 && (
                      <span className="text-gray-400 px-2">...</span>
                    )}
                    <button
                      onClick={() => set_current_page(last_page)}
                      className="px-3 py-2 rounded-lg text-sm font-medium bg-slate-600/40 text-white hover:bg-slate-500/50 border border-slate-500/30 transition-all duration-200"
                    >
                      {last_page}
                    </button>
                  </>
                )}
              </div>

              {/* Next Button */}
              <button
                onClick={() =>
                  set_current_page((prev) => Math.min(last_page, prev + 1))
                }
                disabled={current_page === last_page}
                className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  current_page === last_page
                    ? "bg-slate-700/40 text-gray-500 cursor-not-allowed"
                    : "bg-slate-600/40 text-white hover:bg-slate-500/50 border border-slate-500/30"
                }`}
              >
                <span>Next</span>
                <ChevronRight className="w-4 h-4" />
              </button>

              {/* Oldest Button */}
              <button
                onClick={() => set_current_page(last_page)}
                disabled={current_page === last_page}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  current_page === last_page
                    ? "bg-slate-700/40 text-gray-500 cursor-not-allowed"
                    : "bg-slate-600/40 text-white hover:bg-slate-500/50 border border-slate-500/30"
                }`}
              >
                Oldest
              </button>
            </div>
          ) : (
            /* Single Page - Show simplified pagination info */
            <div className="text-center">
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-slate-600/20 border border-slate-500/30 rounded-lg">
                <span className="text-white text-sm font-medium">
                  Page 1 of 1
                </span>
                <span className="text-gray-400 text-sm">•</span>
                <span className="text-gray-400 text-sm">
                  All transactions shown
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
